import { InvoiceType } from '../types/sync';
import { NCFType } from '../types/ncf';

/**
 * Classification helpers for the comprobante the PLATFORM business issues
 * to a tenant for a subscription payment. Single source of truth shared by
 * the backend emission service, the sadmin config DTO validation, and the
 * sadmin type picker — so the assignable set, the RNC rule, and the
 * NCFType → InvoiceType mapping never drift across surfaces.
 */

/**
 * Forward-sale comprobante types the superadmin may assign to a tenant.
 * Reversal types (B03/B04/E33/E34) and the compra/menor-gasto families are
 * intentionally excluded — a subscription charge is a forward sale. `null`
 * (no assignment) means Pre-Factura (non-fiscal).
 */
export const PLATFORM_ASSIGNABLE_NCF_TYPES: readonly NCFType[] = [
  NCFType.B01, // Crédito Fiscal
  NCFType.E31,
  NCFType.B02, // Consumo
  NCFType.E32,
  NCFType.B14, // Regímenes Especiales
  NCFType.E44,
  NCFType.B15, // Gubernamental
  NCFType.E45,
  NCFType.B16, // Exportaciones
  NCFType.E46,
] as const;

const PLATFORM_ASSIGNABLE_SET = new Set<string>(PLATFORM_ASSIGNABLE_NCF_TYPES);

/** True when `ncfType` is a forward-sale type the platform may assign. */
export function isAssignablePlatformComprobante(
  ncfType: string | null | undefined
): ncfType is NCFType {
  return !!ncfType && PLATFORM_ASSIGNABLE_SET.has(ncfType);
}

/**
 * Consumo (B02/E32) does not require a buyer RNC; every other assignable
 * fiscal type (crédito fiscal, gubernamental, regímenes, exportaciones)
 * legally does. A type that requires an RNC but whose tenant has none is
 * blocked (sadmin banner), never silently downgraded.
 */
export function isRncRequiredComprobante(ncfType: NCFType): boolean {
  return ncfType !== NCFType.B02 && ncfType !== NCFType.E32;
}

/** True for the electronic (e-CF) series — the DGII code starts with `E`. */
export function isElectronicNcf(ncfType: NCFType): boolean {
  return ncfType.startsWith('E');
}

/**
 * Map an assigned forward-sale NCFType to the operational `InvoiceType`
 * the invoice engine expects. The NCFType already encodes the B-vs-E
 * split, so the engine receives the explicit `ncfType` alongside.
 */
export function platformComprobanteInvoiceType(ncfType: NCFType): InvoiceType {
  switch (ncfType) {
    case NCFType.B01:
    case NCFType.E31:
      return InvoiceType.FISCAL_CREDIT;
    case NCFType.B02:
    case NCFType.E32:
      return InvoiceType.CONSUMO;
    case NCFType.B14:
    case NCFType.E44:
      return InvoiceType.SPECIAL_REGIMES;
    case NCFType.B15:
    case NCFType.E45:
      return InvoiceType.GOVERNMENTAL;
    case NCFType.B16:
    case NCFType.E46:
      return InvoiceType.EXPORTS;
    default:
      // Not assignable — caller validates with isAssignablePlatformComprobante first.
      return InvoiceType.CONSUMO;
  }
}

/**
 * The nota de crédito type that reverses a comprobante on the platform:
 * E34 when the original was electronic, else B04. DGII pairs the reversal
 * series to the original's series.
 */
export function platformCreditNoteTypeFor(originalNcfType: NCFType): NCFType {
  return isElectronicNcf(originalNcfType) ? NCFType.E34 : NCFType.B04;
}
